/**
 * Physical and visualization constants shared across the Earth3D prototype.
 *
 * NOTE ON DATA STATUS: wind, temperature, geopotential height and MSLP are
 * REAL NOAA GFS data by default (see src/data/gfsData.ts), with an
 * idealized/synthetic circulation model as a fallback if that data isn't
 * available (see main.ts). Pressure levels below are the REAL set of levels
 * published by NOAA GFS (0.25 deg analysis) that carry complete
 * UGRD/VGRD/DZDT/HGT records.
 */

/** Mean Earth radius in kilometers (sphere approximation is sufficient at this scale). */
export const EARTH_RADIUS_KM = 6371;

/**
 * Donate link shown in the control panel, if set -- a direct PayPal hosted payment page link
 * (no external SDK script to load, so nothing to fail/block if PayPal's CDN is unreachable).
 * Left blank to hide the donate button entirely rather than render a broken link.
 */
export const DONATE_URL = "https://www.paypal.com/ncp/payment/TG7G4NYU67M8A";

/**
 * All 41 GFS pressure levels (hPa) confirmed to carry UGRD, VGRD, DZDT and HGT
 * records in the NOAA 0.25deg analysis inventory, ordered surface-to-top.
 */
export const GFS_PRESSURE_LEVELS_HPA: readonly number[] = [
  1000, 975, 950, 925, 900, 850, 800, 750, 700, 650, 600, 550, 500, 450, 400,
  350, 300, 250, 200, 150, 100, 70, 50, 40, 30, 20, 15, 10, 7, 5, 3, 2, 1, 0.7,
  0.4, 0.2, 0.1, 0.07, 0.04, 0.02, 0.01
];

/** Default visible level range, named so min/max always refers to the hPa value itself
 *  (minHpa = smallest pressure = top of range; maxHpa = largest pressure = bottom/surface). */
export const DEFAULT_MIN_HPA = 10;
export const DEFAULT_MAX_HPA = 1000;

/** Default terrain/atmosphere height exaggeration factor (shared by both, per user request). */
export const DEFAULT_HEIGHT_EXAGGERATION = 30;
export const MIN_HEIGHT_EXAGGERATION = 1;
export const MAX_HEIGHT_EXAGGERATION = 60;

/** Default vertical-motion exaggeration factor, independent of height exaggeration. */
export const DEFAULT_VERTICAL_MOTION_EXAGGERATION = 11;
export const MIN_VERTICAL_MOTION_EXAGGERATION = 1;
export const MAX_VERTICAL_MOTION_EXAGGERATION = 121;

/** Approximate top of the visualized atmosphere (matches the 0.01 hPa level), in km. */
export const ATMOSPHERE_TOP_KM = 80;

export const PARTICLE_COUNT = 36000;
/** Particles are re-seeded at a random location after this many seconds, so streaks stay bounded. */
export const PARTICLE_MAX_AGE_SECONDS = 25;
/** Number of recent positions kept per particle to draw its fading motion trail. */
export const PARTICLE_TRAIL_LENGTH = 20;
/** Scene background color; particle trails fade toward this color as they age. */
export const SCENE_BACKGROUND_HEX = 0x05070d;

/**
 * Time constant (seconds) for the slow-following "baseline" altitude each particle tracks.
 * Vertical motion exaggeration amplifies only the (small, real) deviation from this baseline,
 * not the baseline itself -- see particles.ts for why this keeps height exaggeration and
 * vertical motion exaggeration from compounding into the physics-sampling altitude.
 */
export const ALTITUDE_SMOOTHING_TAU_SECONDS = 4;

/**
 * Time constant (seconds) for low-pass filtering the SAMPLED vertical velocity before it's
 * integrated into altitude. Real GFS DZDT is noisy at grid scale (much more than wind or
 * temperature); without this filter, that grid-scale noise gets massively amplified by
 * vertical motion exaggeration and shows up as high-frequency "ripples" in particle motion.
 * Shorter than ALTITUDE_SMOOTHING_TAU_SECONDS so genuine larger-scale vertical motion still
 * comes through, just with the highest-frequency noise smoothed away.
 */
export const WZ_SMOOTHING_TAU_SECONDS = 2;

/** Density-grid binning used to keep particle coverage roughly uniform (see particleDensityGrid.ts). */
export const DENSITY_GRID_LAT_BANDS = 24;
export const DENSITY_GRID_BASE_LON_BINS = 48;
/** Per-second probability that a particle sitting in an overloaded cell is retired early. */
export const OVERLOAD_RETIRE_RATE_PER_SECOND = 0.3;

/**
 * Rebuilding the density grid's occupancy histogram is an O(particle count) pass over every
 * particle, done unconditionally every frame -- at large particle counts this becomes a real
 * cost for a signal that doesn't need per-frame freshness (density distribution changes slowly
 * relative to frame rate). Recomputed only every Nth frame instead; particles are retired
 * probabilistically anyway (OVERLOAD_RETIRE_RATE_PER_SECOND), so a few frames of staleness here
 * is imperceptible.
 */
export const DENSITY_GRID_RECOMPUTE_EVERY_N_FRAMES = 6;

/**
 * Low-pressure systems (cyclones/hurricanes) are exactly where advection is most interesting to
 * see, but they're also naturally CONVERGENT -- particles spiral inward and cluster there -- so
 * the uniform-density targeting above would otherwise actively fight that clustering, retiring
 * particles as "overloaded" right where they're most informative. Instead, each density-grid
 * cell's target/overload density is boosted based on its (fixed, precomputed) MSLP: cells at or
 * above LOW_PRESSURE_DENSITY_BOOST_START_HPA get no boost (1x, the uniform baseline), ramping up
 * to LOW_PRESSURE_DENSITY_BOOST_FACTOR by LOW_PRESSURE_DENSITY_BOOST_FULL_HPA. This both biases
 * respawns toward filling these cells up further AND raises how crowded they can get before
 * particles are retired as overloaded.
 */
export const LOW_PRESSURE_DENSITY_BOOST_START_HPA = 1005;
export const LOW_PRESSURE_DENSITY_BOOST_FULL_HPA = 960;
export const LOW_PRESSURE_DENSITY_BOOST_FACTOR = 4;

/**
 * The density-grid cells above are coarse (tens of degrees wide near the equator) -- much wider
 * than a small/tight hurricane, which can be thinner than a single cell. Sampling only each
 * cell's CENTER point for its MSLP boost can miss such a storm entirely if it's off-center, or
 * dilute it into "looks like a normal cell" if it's small relative to the cell. Each cell's MSLP
 * is instead taken as the MINIMUM found over a small sub-grid spanning the cell (see
 * precomputeDensityMultipliers in particleDensityGrid.ts), so an embedded small/off-center low
 * still gets properly detected and boosted.
 */
export const DENSITY_GRID_SUBSAMPLES_PER_AXIS = 4;

/**
 * Per-particle persistence shield: while a particle sits in a low-pressure zone (its OWN
 * precise local MSLP, not a coarse cell average), it's immune to density-based ("overloaded")
 * early retirement for a duration that scales with how deep that local pressure is --
 * MIN_PERSISTENCE_SECONDS at/just-below LOW_PRESSURE_PERSISTENCE_START_HPA, ramping up to
 * MAX_PERSISTENCE_SECONDS by LOW_PRESSURE_PERSISTENCE_FULL_HPA. Depth is used as a (deliberately
 * cheap, no-extra-sampling) proxy for "small and intense": small, vigorous systems (hurricanes)
 * typically have much lower central pressure than large, gentle ones, so deeper local pressure
 * reasonably approximates "this is the kind of tight, interesting feature worth lingering in" --
 * giving smaller/more intense lows a stronger, longer-lasting persistence effect than large
 * shallow ones, independent of (and complementing) the coarser cell-based boost above.
 */
export const LOW_PRESSURE_PERSISTENCE_START_HPA = 1005;
export const LOW_PRESSURE_PERSISTENCE_FULL_HPA = 950;
export const MIN_PERSISTENCE_SECONDS = 2;
export const MAX_PERSISTENCE_SECONDS = 18;

/**
 * Sampling MSLP for every particle every frame (to grant/refresh the persistence shield above)
 * adds up at large particle counts. Persistence grants last several seconds, so there's no need
 * to check any given particle more than a few times a second -- each particle is instead
 * checked only once every this-many frames (staggered by particle index so the work is spread
 * evenly across frames rather than all particles being checked in the same frame).
 */
export const PERSISTENCE_SAMPLE_STAGGER_FRAMES = 6;

/**
 * How long (consecutive seconds) a particle can sit pinned against the terrain-clip floor (see
 * currentWorldPosition() in particles.ts) before it's retired -- rather than visually resting
 * flattened against a mountainside indefinitely, which grows more common/noticeable at high
 * vertical motion exaggeration (near-surface particles over elevated terrain are already close
 * to the clamp floor; any further downward wobble immediately hits it).
 */
export const GROUNDED_RETIRE_SECONDS = 1.5;

/**
 * Margin (km, in true/unexaggerated altitude) a particle is allowed to drift beyond the
 * selected pressure range before it's retired as out-of-bounds. Strong real updrafts (e.g. a
 * hurricane eyewall) can carry a particle past the range boundary quickly; a generous margin
 * lets that upward spiral read clearly before the particle finally leaves, rather than vanishing
 * almost immediately.
 */
export const ALTITUDE_OUT_OF_BOUNDS_MARGIN_KM = 3;

/**
 * Particles fade toward this opacity multiplier (relative to their normal opacity) as they
 * approach the TOP of the currently selected pressure range, and are fully opaque (1x) at the
 * BOTTOM of it -- higher/thinner air reads as more ethereal, and the gradient is always relative
 * to whatever range is currently being viewed so it stays visible regardless of range selection.
 */
export const MIN_ALTITUDE_OPACITY_FACTOR = 0.2;

/**
 * When a particle is due to respawn (aged out, drifted out of range, or retired for density
 * balancing), its trail fades to invisible over this many seconds BEFORE it actually jumps to
 * a new location, instead of the whole trail vanishing in a single frame.
 */
export const PARTICLE_FADE_OUT_SECONDS = 1.2;

/**
 * Minimum radial gap (scene units, same units as EARTH_RADIUS_UNITS) a particle is kept above
 * the local (equally height-exaggerated) terrain surface -- see the soft floor in
 * currentWorldPosition() in particles.ts.
 */
export const PARTICLE_TERRAIN_CLEARANCE_UNITS = 0.01;

/**
 * Depth (real km, exaggerated the same way terrain is -- i.e. scaled by heightExaggeration, NOT
 * verticalMotionExaggeration) of the "cushioning" zone just above the terrain floor, within
 * which a particle's descent is smoothly decelerated rather than hard-clamped. Standard-
 * atmosphere altitude is sea-level-referenced, not ground-relative, so near the surface --
 * especially over elevated terrain, and especially once vertical motion exaggeration amplifies
 * a particle's small real deviation into a large display one -- a particle's unclamped
 * display radius can end up far below the terrain mesh. A hard clamp there creates a visible
 * discontinuity: the radius snaps flat (vertical motion stops dead) while lat/lon keeps
 * advecting at full strength every frame regardless, reading as "crashes into the ground, then
 * suddenly slides horizontally". The soft floor below instead asymptotically approaches the
 * terrain radius (never reaching, let alone passing, it) over this buffer depth, so the
 * deceleration itself is what's visible, not a snap.
 */
export const PARTICLE_TERRAIN_SOFT_FLOOR_KM = 6;

/**
 * A particle is considered "grounded" (for GROUNDED_RETIRE_SECONDS purposes) once the soft
 * floor above has compressed it to within this fraction of its buffer depth from the terrain
 * radius -- i.e. visually indistinguishable from resting on the floor, even though the soft
 * floor function never lets it mathematically reach it.
 */
export const GROUNDED_PROXIMITY_FRACTION = 0.05;

/**
 * After a respawn, a particle fades IN from invisible over this many seconds rather than
 * appearing abruptly at full opacity/size. Without this, a particle that happens to respawn
 * into a slow-wind area (which renders as a large head marker -- see HEAD_POINT_*_SIZE_PX)
 * pops in instantly at full size, reading as a distracting "sparkle" among thousands of
 * particles respawning continuously. Deliberately longer than the fade-out so new particles
 * ease in gently.
 */
export const PARTICLE_FADE_IN_SECONDS = 1.8;

/**
 * Head markers and trails are capped at this opacity (rather than fully opaque) so particles
 * visually blend a little with the terrain/MSLP surface beneath them instead of reading as
 * solid, flat dots/lines.
 */
export const PARTICLE_MAX_OPACITY = 0.85;

/**
 * Fast particles cover more ground per frame, so their fixed-length history trail reads as a
 * long streak; slow particles barely move, so the same trail reads as a near-invisible dot.
 * To balance how much visual "space" each particle occupies regardless of speed, a round head
 * marker is drawn at each particle's current position, sized LARGER for slow particles and
 * SMALLER for fast ones (whose long trail already carries the visual weight). Sizes are in
 * approximate screen pixels at the globe's surface.
 */
export const HEAD_POINT_MIN_SIZE_PX = 2;
export const HEAD_POINT_MAX_SIZE_PX = 10;
/** Horizontal wind speed (m/s) at which the head marker sits roughly halfway between min/max size. */
export const HEAD_POINT_REFERENCE_SPEED_MS = 8;

/**
 * Trail width (in the same approximate screen-pixel units as the head marker) is this fraction
 * of that particle's current head marker size, so a particle's tail reads as visually
 * continuous with its head rather than a fixed-width line regardless of particle size.
 */
export const TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE = 0.55;

/**
 * Scene scale: 1 Three.js unit = this many kilometers. Chosen so the (unexaggerated)
 * Earth radius is a convenient ~6.37 scene units.
 */
export const KM_PER_SCENE_UNIT = 1000;
export const EARTH_RADIUS_UNITS = EARTH_RADIUS_KM / KM_PER_SCENE_UNIT;

/**
 * Visualization pacing constant (simulated seconds advanced per real second per m/s of wind),
 * tuned purely for a legible animation speed. It is applied identically to u, v and w
 * components, so it does NOT distort the physical ratio between horizontal and vertical
 * motion -- only the separate "vertical motion exaggeration" control does that, intentionally.
 */
export const SIMULATION_TIME_SCALE = 6000;


